import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE, formatApiError, useAuth } from "../components/AuthContext";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
  Link as LinkIcon, RefreshCw, Loader2, Plug, AlertTriangle, CheckCircle2,
  ArrowRight, Beaker,
} from "lucide-react";

const PROVIDERS = [
  { id: "xero", name: "Xero",
    blurb: "Push contacts + invoices to your Xero tenant; pull payment status back automatically.",
    accent: "var(--accent)" },
  { id: "qbo", name: "QuickBooks Online",
    blurb: "Push contacts + invoices to QBO; pull payment status back automatically.",
    accent: "var(--moss)" },
];

const fmtTs = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

export default function AccountingSync() {
  const { state } = useAuth();
  const isAdmin = ["owner", "manager"].includes(state?.user?.role);
  const [connections, setConnections] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [busy, setBusy] = useState(null);  // provider id while a button is spinning
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);

  const reload = async () => {
    setLoading(true);
    try {
      const [c, cf, cli, inv] = await Promise.all([
        axios.get(`${API_BASE}/accounting/connections`),
        axios.get(`${API_BASE}/accounting/conflicts?unresolved_only=true`),
        axios.get(`${API_BASE}/clients`),
        axios.get(`${API_BASE}/invoices`),
      ]);
      setConnections(c.data || []);
      setConflicts(cf.data || []);
      setClients(cli.data || []);
      setInvoices(inv.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const byProvider = (p) => connections.find((c) => c.provider === p) || null;

  const connect = async (provider) => {
    setBusy(provider);
    try {
      await axios.post(`${API_BASE}/accounting/connect`, { provider });
      toast.success(`${provider.toUpperCase()} connected (stub)`);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const disconnect = async (provider) => {
    if (!window.confirm(`Disconnect ${provider.toUpperCase()}? Future syncs will pause until you reconnect.`)) return;
    setBusy(provider);
    try {
      await axios.delete(`${API_BASE}/accounting/connect/${provider}`);
      toast.success(`${provider.toUpperCase()} disconnected`);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const syncNow = async (provider) => {
    setBusy(provider);
    try {
      const r = await axios.post(`${API_BASE}/accounting/sync`, { provider });
      const s = r.data?.[provider]?.summary;
      if (s) {
        toast.success(`${provider.toUpperCase()} synced · ${s.contacts_pushed || 0} contacts · ${s.invoices_pushed || 0} invoices · ${s.payments_pulled || 0} payments pulled · ${s.conflicts || 0} conflicts`);
      } else {
        toast.error(`${provider.toUpperCase()} sync skipped`);
      }
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const resolveConflict = async (conf, resolution) => {
    try {
      await axios.post(`${API_BASE}/accounting/conflicts/${conf.conflict_id}/resolve`, { resolution });
      toast.success(`Resolved · kept ${resolution === "ours" ? "our copy" : "their copy"}`);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!isAdmin) {
    return (
      <div className="p-6 text-[13px] text-[var(--ink-3)]">
        Only owners or managers can manage accounting integrations.
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-8" data-testid="accounting-sync-page">
      <div>
        <div className="overline mb-1 inline-flex items-center gap-1.5">
          <Plug size={11}/> Settings · Accounting integrations
        </div>
        <h1 className="font-heading text-4xl">
          Sync your <span className="text-[var(--accent)] italic">books</span>
        </h1>
        <p className="text-[13px] text-[var(--ink-3)] mt-2 max-w-2xl">
          Two-way sync of clients and invoices with Xero and QuickBooks Online. New invoices push as drafts;
          payment status flows back. Every change is reconciled every 15 minutes — or hit Sync now to refresh.
        </p>
        <div className="text-[11px] text-[var(--ink-3)] mt-1 inline-flex items-center gap-1 italic">
          <Beaker size={10}/> First slice ships against a stub provider for demos — wiring up real OAuth tenants is a one-line config change.
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {PROVIDERS.map((p) => {
          const conn = byProvider(p.id);
          const connected = conn?.status === "connected";
          const summary = conn?.last_sync_summary || {};
          return (
            <div key={p.id} className="card p-5" data-testid={`provider-card-${p.id}`}
                 style={{ borderTop: `3px solid ${p.accent}` }}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <div className="font-heading text-xl">{p.name}</div>
                  <div className="text-[11.5px] text-[var(--ink-3)] mt-0.5">{p.blurb}</div>
                </div>
                <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full"
                      style={{ background: connected ? "var(--subtle-strong)" : "var(--subtle)",
                               color: connected ? "var(--moss)" : "var(--ink-3)" }}
                      data-testid={`provider-status-${p.id}`}>
                  {connected ? "connected" : (conn ? "disconnected" : "not connected")}
                </span>
              </div>

              {connected && (
                <div className="mt-3 mb-3 p-3 rounded-md bg-[var(--subtle)] border border-[var(--border)] text-[11.5px] text-[var(--ink-2)]">
                  <div className="flex items-center justify-between">
                    <span className="text-[var(--ink-3)]">Tenant</span>
                    <span className="font-medium">{conn.tenant_name || "—"}</span>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[var(--ink-3)]">Last sync</span>
                    <span className="font-mono">{fmtTs(conn.last_sync_at)}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 mt-2">
                    <span className="text-[var(--ink-3)]">Contacts pushed</span>
                    <span className="font-mono text-right">{summary.contacts_pushed || 0}</span>
                    <span className="text-[var(--ink-3)]">Invoices pushed</span>
                    <span className="font-mono text-right">{summary.invoices_pushed || 0}</span>
                    <span className="text-[var(--ink-3)]">Payments pulled</span>
                    <span className="font-mono text-right">{summary.payments_pulled || 0}</span>
                    <span className="text-[var(--ink-3)]">Conflicts</span>
                    <span className="font-mono text-right">{summary.conflicts || 0}</span>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2 mt-3">
                {!connected && (
                  <button onClick={() => connect(p.id)} disabled={busy === p.id}
                          className="btn-primary text-[12px] disabled:opacity-50 inline-flex items-center gap-1"
                          data-testid={`provider-connect-${p.id}`}>
                    {busy === p.id ? <Loader2 size={12} className="animate-spin"/> : <LinkIcon size={12}/>}
                    Connect (stub)
                  </button>
                )}
                {connected && (
                  <>
                    <button onClick={() => syncNow(p.id)} disabled={busy === p.id}
                            className="btn-primary text-[12px] disabled:opacity-50 inline-flex items-center gap-1"
                            data-testid={`provider-sync-${p.id}`}>
                      {busy === p.id ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>}
                      Sync now
                    </button>
                    <button onClick={() => disconnect(p.id)} disabled={busy === p.id}
                            className="text-[12px] text-[var(--ink-3)] hover:text-[#B91C1C] disabled:opacity-50"
                            data-testid={`provider-disconnect-${p.id}`}>
                      Disconnect
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <section data-testid="conflicts-section">
        <div className="overline mb-2 inline-flex items-center gap-1.5">
          <AlertTriangle size={11}/> Open conflicts
        </div>
        {loading ? (
          <div className="text-[12px] text-[var(--ink-3)]">Loading…</div>
        ) : conflicts.length === 0 ? (
          <div className="card p-5 text-[12.5px] text-[var(--ink-3)] italic inline-flex items-center gap-2"
               data-testid="conflicts-empty">
            <CheckCircle2 size={12} className="text-[var(--moss)]"/>
            No conflicts. The next 15-minute cron will keep both systems aligned.
          </div>
        ) : (
          <div className="space-y-2">
            {conflicts.map((cf) => (
              <div key={cf.conflict_id} className="card p-4"
                   data-testid={`conflict-row-${cf.conflict_id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[12.5px] text-[var(--ink-2)]">
                    <span className="font-medium">{cf.entity_kind}</span>
                    <span className="text-[var(--ink-3)] mx-1.5">·</span>
                    <span className="uppercase text-[10px] tracking-wider text-[var(--ink-3)]">{cf.provider}</span>
                    <span className="text-[var(--ink-3)] mx-1.5">·</span>
                    <span className="text-[11px] text-[var(--ink-3)]">{fmtTs(cf.created_at)}</span>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-[var(--ink-3)]">
                    auto-pick: {cf.chosen_winner}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-[12px]">
                  <div className="p-2.5 rounded-md bg-[var(--subtle)] border border-[var(--border)]">
                    <div className="overline mb-1">Our copy</div>
                    <pre className="whitespace-pre-wrap font-mono text-[11px] text-[var(--ink-2)]">{JSON.stringify(cf.our_snapshot, null, 2)}</pre>
                  </div>
                  <div className="p-2.5 rounded-md bg-[var(--subtle)] border border-[var(--border)]">
                    <div className="overline mb-1">Their copy</div>
                    <pre className="whitespace-pre-wrap font-mono text-[11px] text-[var(--ink-2)]">{JSON.stringify(cf.their_snapshot, null, 2)}</pre>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2 mt-3">
                  <button onClick={() => resolveConflict(cf, "ours")}
                          className="text-[12px] text-[var(--ink-2)] hover:text-[var(--accent)] inline-flex items-center gap-1"
                          data-testid={`conflict-keep-ours-${cf.conflict_id}`}>
                    Keep ours <ArrowRight size={11}/>
                  </button>
                  <button onClick={() => resolveConflict(cf, "theirs")}
                          className="btn-primary text-[12px]"
                          data-testid={`conflict-keep-theirs-${cf.conflict_id}`}>
                    Keep theirs
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <DevSimPanel
        clients={clients} invoices={invoices} connections={connections}
        onAfter={reload}
        data-testid="dev-sim-panel"
      />
    </div>
  );
}

function DevSimPanel({ clients, invoices, connections, onAfter }) {
  const connectedProviders = connections.filter((c) => c.status === "connected").map((c) => c.provider);
  const [provider, setProvider] = useState(connectedProviders[0] || "xero");
  const [clientId, setClientId] = useState("");
  const [invoiceId, setInvoiceId] = useState("");
  const [editName, setEditName] = useState("");

  useEffect(() => {
    if (connectedProviders.length > 0 && !connectedProviders.includes(provider)) {
      setProvider(connectedProviders[0]);
    }
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [connections]);

  // Only show invoices that have been pushed to the selected provider
  const pushedInvoices = invoices.filter(
    (inv) => inv.external_refs?.[provider]?.external_id && inv.status !== "paid"
  );
  // Only show clients that have been pushed to the selected provider
  const pushedClients = clients.filter(
    (c) => c.external_refs?.[provider]?.external_id
  );

  const fire = async (path, body) => {
    try {
      await axios.post(`${API_BASE}${path}`, body);
      toast.success("Simulated · re-sync to see the effect");
      onAfter();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (connectedProviders.length === 0) return null;

  return (
    <section className="border-t border-dashed border-[var(--border)] pt-6" data-testid="dev-sim-panel">
      <div className="overline mb-2 inline-flex items-center gap-1.5">
        <Beaker size={11}/> Demo · simulate "the other side"
      </div>
      <div className="text-[11.5px] text-[var(--ink-3)] mb-3 max-w-2xl">
        These controls only exist while the stub provider is live — they push edits into the simulated
        Xero / QBO tenant so you can see how conflict detection and payment-pull work. They are gated
        to admins and disappear once a real OAuth tenant is connected.
      </div>
      <div className="card p-4 space-y-4">
        <div className="flex items-center gap-2">
          <label className="text-[11px] text-[var(--ink-3)]">Provider</label>
          <Select value={provider} onValueChange={setProvider}>
            <SelectTrigger className="h-8 w-32 bg-white border-[var(--border)] text-[12px]"
                           data-testid="sim-provider-select">
              <SelectValue/>
            </SelectTrigger>
            <SelectContent>
              {connectedProviders.map((p) => (
                <SelectItem key={p} value={p} className="text-[12px]">{p.toUpperCase()}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="text-[12px] font-medium">Mark an invoice paid externally</div>
            <Select value={invoiceId} onValueChange={setInvoiceId}>
              <SelectTrigger className="w-full bg-white border-[var(--border)] text-[12px]"
                             data-testid="sim-invoice-select">
                <SelectValue placeholder="Pick an invoice…"/>
              </SelectTrigger>
              <SelectContent>
                {pushedInvoices.map((inv) => (
                  <SelectItem key={inv.invoice_id} value={inv.invoice_id} className="text-[12px]">
                    {inv.number || inv.invoice_id} · ${inv.total}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {pushedInvoices.length === 0 && (
              <div className="text-[10.5px] text-[var(--ink-3)] italic">
                No pushed invoices on this provider. Send an invoice + Sync first.
              </div>
            )}
            <button onClick={() => fire("/accounting/_dev/simulate-payment",
                                          { provider, invoice_id: invoiceId })}
                    disabled={!invoiceId}
                    className="btn-primary text-[12px] disabled:opacity-50"
                    data-testid="sim-pay-btn">
              Simulate payment
            </button>
          </div>

          <div className="space-y-2">
            <div className="text-[12px] font-medium">Edit a contact externally</div>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger className="w-full bg-white border-[var(--border)] text-[12px]"
                             data-testid="sim-client-select">
                <SelectValue placeholder="Pick a client…"/>
              </SelectTrigger>
              <SelectContent>
                {pushedClients.map((c) => (
                  <SelectItem key={c.client_id} value={c.client_id} className="text-[12px]">
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)}
                   placeholder="New name from the external system"
                   className="w-full bg-white border border-[var(--border)] rounded-md px-2 py-1.5 text-[12px]"
                   data-testid="sim-edit-name"/>
            <button onClick={() => fire("/accounting/_dev/simulate-contact-edit",
                                          { provider, client_id: clientId, name: editName })}
                    disabled={!clientId || !editName.trim()}
                    className="btn-primary text-[12px] disabled:opacity-50"
                    data-testid="sim-edit-btn">
              Simulate external edit
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
